import { useState } from "react";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import { useTranslation } from "@/lib/i18n";
import { AddFab } from "@/features/admin/components";
import CreateWidgetDialog from "@/features/widget-forms/dialogs/create-widget-dialog";
import { usePasteWidget } from "@/features/admin/features/widgets/hooks/use-paste-widget";
import { WidgetClipboardLayout } from "@/features/admin/features/widgets/widget-clipboard";
import { FormValues } from "@/types/widget";

interface PasteWidgetFabProps {
  pageId: string;
}

/**
 * Floating action button that pastes a widget from the clipboard as a new
 * container on the page. The pre-fill dialog and clipboard handling live here so
 * the edit page stays free of paste-specific state. The copied container size
 * travels on the create request, so no follow-up layout mutation is needed.
 */
export const PasteWidgetFab = ({ pageId }: PasteWidgetFabProps) => {
  const { t } = useTranslation();
  const readPastedWidget = usePasteWidget();

  const [pastedValues, setPastedValues] = useState<FormValues>();
  const [pastedLayout, setPastedLayout] = useState<WidgetClipboardLayout>();
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);

  const handlePaste = async () => {
    const parsed = await readPastedWidget();
    if (!parsed) return;

    setPastedValues(parsed.values);
    setPastedLayout(parsed.layout);
    setPasteDialogOpen(true);
  };

  return (
    <>
      <AddFab
        tooltip={t("admin.pages.detail.paste_widget")}
        icon={<ContentPasteIcon />}
        bottom={88}
        onClick={handlePaste}
      />
      {pastedValues && (
        <CreateWidgetDialog
          pageId={pageId}
          pastedValues={pastedValues}
          pastedLayout={pastedLayout}
          open={pasteDialogOpen}
          onOpenChange={(value) => {
            setPasteDialogOpen(value);
            if (!value) {
              setPastedValues(undefined);
              setPastedLayout(undefined);
            }
          }}
        />
      )}
    </>
  );
};
